import { Component, createRef, type ImgHTMLAttributes } from 'react'
import { useParams } from 'react-router-dom'

import { hls as apiHls, iziplaySubtitle, tmdb } from '../tools/api'

import Query from '../tools/query'

import './hlsplayer.css'
import { SubtitleModal } from '../components/modalSubtitle'
import '@videojs/react/video/skin.css'
import { VideoPlayer, VideoSkin } from '@videojs/react/video'
import { HlsJsVideo } from '@videojs/react/media/hlsjs-video'
import type { HlsJsAdapter } from '@videojs/hlsjs-video'
import { displayNotification, getLang, setCurrentPath } from '../state/ui'
import { rtSend } from '../state/ws'
import { loadSettings } from '../queries/client'
import {
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  LinearProgress,
  Select,
  Step,
  StepLabel,
  Stepper,
  Typography
} from '@iziplay/ui-kit'
import { Cast, CastConnected, Pause, PlayArrow, UploadFile } from '@nine-thirty-five/material-symbols-react/sharp/filled'
import { castSend } from '../tools/cast'


interface HlsPlayerProps {
  params: { file: string } & Record<string, string | undefined>
}

class HlsPlayer extends Component<HlsPlayerProps, any> {
  constructor(props: HlsPlayerProps) {
    super(props)

    this.state = {
      isSubtitleModalOpen: false,
      server: this.q.server
        ? `${this.q.server}/v1`
        : (import.meta.env.REACT_APP_IZIPLAY_URL_HLS as string),
      playerId: Date.now(),
      subs: [],
      step: -1,
      ffprobe: null,
      castStatus: {}
    }
    console.log(`Use file ${this.state.server}/${this.props.params.file}/master.m3u8`)

    let device = 'web'
    if (window.location.host.indexOf('staging') !== -1) device = 'web-staging'
    if (window.location.host.indexOf('localhost') !== -1) device = 'web-localhost'

    this.media = {
      type: this._isTvShow() ? 'tvshow' : 'movie',
      tvdb: this.q.tvdb,
      tmdb: this.q.tmdb,
      tmdb_episode: this.q.tmdb_episode,
      imdb: this.q.imdb,
      season: this.q.season,
      episode: this.q.episode,
      device,
      file: this.props.params.file
    }

    this.connectWs()

    this.interval = setInterval(() => {
      if (this.video.current) {
        if (this.video.current && this.time === this.video.current?.currentTime) {
          return
        }
        this.time = this.video.current?.currentTime
      }
      this.media.time = this.time
      this.media.progress = (this.time / this.probeDuration) * 100
      rtSend('viewing', this.media)
    }, 4000)
  }

  video = createRef<HlsJsAdapter>()
  q = Query.parse(window.location.search)
  gettingSubs = false
  probeDuration: number = 1

  media = {} as any

  time = 0
  interval: NodeJS.Timeout | null = null
  ws: WebSocket | null = null

  /*
   * The pipeline reports fine-grained events; the stepper shows five stages.
   * `stopped` is not a stage: it flags a transcoder that quit early.
   */
  stages: { label: string; events: string[] }[] = [
    { label: 'Starting', events: ['start'] },
    { label: 'Downloading', events: ['downloadStart', 'downloading'] },
    { label: 'Probing', events: ['probeStart', 'probeEnd'] },
    { label: 'Transcoding', events: ['transcoderStart', 'transcoding'] },
    { label: 'Finished', events: ['transcoderEnd'] }
  ]

  connectWs() {
    this.ws = new window.WebSocket(
      `${this.state.server.replace('https', 'wss').replace('http', 'ws')}/${
        this.props.params.file
      }/ws`
    )
    this.ws.onmessage = msg => {
      const json = JSON.parse(msg.data)
      if (typeof json.error !== 'undefined' || json.type === 'error') {
        displayNotification(json.error || json.value.message, 'error')
      }
      const step = this.stages.findIndex(stage => stage.events.includes(json.type))
      if (step > this.state.step) {
        this.setState({ step })
      }

      this.setState({ [json.type]: json.value })

      if (json.type === 'probeEnd') {
        this.ffprobe()
      }
    }
    this.ws.onclose = event => {
      if (event.code === 1006) return
      this.connectWs()
    }
  }

  componentDidMount() {
    apiHls(this.state.server)
      .get(`/${this.props.params.file}/isavailable`)
      .then(() => {
        console.log('File already started in server')
        this._initPlayer(true)
      })
      .catch(err => {
        console.log(err)
        switch (err.response?.status) {
          case 404:
            console.log('File does not exist on server, try to start it')
            apiHls(this.state.server)
              .put(`/${this.props.params.file}`)
              .then(() => {
                this._initPlayer()
              })
            break
          case 406:
            console.log('File is preparing')
            this._initPlayer()
            break
          default:
            displayNotification('Unknown error', 'error')
            console.log('Unknown error')
            console.log(err)
        }
      })

    setCurrentPath([this.props.params.file])
  }

  async startCast() {
    const castSession = (window as any).cast.framework.CastContext.getInstance().getCurrentSession()
    console.log((window as any).cast)
    console.log(castSession)
    const uri = `${this.state.server}/${this.props.params.file}/index.m3u8`.replace(
      'http:',
      ''
    ).replace('https:', '')

    console.log('Ask chromecast to start', uri)
    const mediaInfo = new (window as any).chrome.cast.media.MediaInfo(
      uri,
      'application/vnd.apple.mpegurl'
    )

    mediaInfo.tracks = []
    mediaInfo.startAbsoluteTime = 0
    if (this._isTvShow()) {
      const tvShow = await tmdb.get<TMDB.TvShow>(`tv/${this.q.tmdb}?language=${getLang()}`)
      const episode = await tmdb.get<TMDB.Episode>(`tv/${this.q.tmdb}/season/${this.q.season}/episode/${this.q.episode}?language=${getLang()}`)
      mediaInfo.metadata = new (window as any).chrome.cast.media.TvShowMediaMetadata()
      mediaInfo.metadata.seriesTitle = tvShow.data.name
      mediaInfo.metadata.season = parseInt(this.q.season, 10)
      mediaInfo.metadata.episode = parseInt(this.q.episode, 10)
      mediaInfo.metadata.serieTitle = episode.data.name
    } else {
      const movie = await tmdb.get<TMDB.Movie>(`movie/${this.q.tmdb}?language=${getLang()}`)
      mediaInfo.metadata = new (window as any).chrome.cast.media.MovieMediaMetadata()
      mediaInfo.metadata.title = movie.data.title
      mediaInfo.metadata.subtitle = 'Powered by Iziplay'
      mediaInfo.metadata.images = [new (window as any).chrome.cast.Image(movie.data.poster_path)]
      mediaInfo.metadata.studio = movie.data.production_companies.map(c => c.name).join(', ')
      mediaInfo.metadata.releaseDate = movie.data.release_date
    }

    this.state.subs.forEach((sub: any, i: number) => {
      // @ts-ignore
      const subtitle = new window.chrome.cast.media.Track(i + 1, window.chrome.cast.media.TrackType.TEXT)
      subtitle.trackContentId = sub.url
      subtitle.trackContentType = 'text/vtt'
      // @ts-ignore
      subtitle.subtype = window.chrome.cast.media.TextTrackType.SUBTITLES
      subtitle.name = sub.lang
      subtitle.language = sub.langId
      mediaInfo.tracks.push(subtitle)
    })
    // @ts-ignore
    const request = new window.chrome.cast.media.LoadRequest(mediaInfo)
    if (this.state.subs[0]) {
      request.activeTrackIds = [1]
    }
    request.customData = this.media

    castSession.loadMedia(request).then(
      () => {
        // @ts-ignore
        const player = new window.cast.framework.RemotePlayer()
        // @ts-ignore
        const playerController = new window.cast.framework.RemotePlayerController(player)
        // @ts-ignore
        window.castPlayer = player
        // @ts-ignore
        window.castPlayerController = playerController
        this.setState({
          launched: true,
          castSession,
          player,
          playerController
        })

        playerController.addEventListener(
          // @ts-ignore
          window.cast.framework.RemotePlayerEventType.PLAYER_STATE_CHANGED,
          (event: any) => {
            console.log('update', event)
            const { castStatus } = this.state
            castStatus[event.field] = event.value

            this.setState({ castStatus })
          }
        )

        castSend({ type: 'viewing', data: this.media })
      },
      (errorCode: Error) => {
        console.log(`Error code: ${errorCode}`)
      }
    )
  }

  castPlayPause() {
    this.state.playerController.playOrPause()
  }

  changeCastSub(id: number) {
    // @ts-ignore
    const req = new window.chrome.cast.media.EditTracksInfoRequest([id])
    this.state.castSession.getMediaSession().editTracksInfo(
      req,
      () => console.log('good'),
      () => console.log('bad')
    )
  }

  openSubtitleModal() {
    this.setState({
      isSubtitleModalOpen: true
    })
  }

  closeSubtitleModal() {
    this.setState({
      isSubtitleModalOpen: false
    })
  }

  componentWillUnmount() {
    rtSend('stop', {
      progress: ((this.video.current?.currentTime ?? Infinity) / this.probeDuration) * 100,
      ...this.media
    })
    this.ws?.close()
    if (this.interval) {
      clearInterval(this.interval)
    }
  }

  _isTvShow() {
    return this.q.season && this.q.episode
  }

  _initPlayer(alreadyDownloaded = false) {
    if (alreadyDownloaded) {
      this.ffprobe()
    }

    if (!this.gettingSubs) {
      this.gettingSubs = true
      loadSettings()
        .then(settings => settings.subtitles ?? [])
        .catch(() => [])
        .then(languages => Promise.all(languages.map(language =>
          iziplaySubtitle()
            .get(`/file/${this.props.params.file}?lng=${language}`)
            .then(res =>
              res.data.map((sub: any, i: number) => ({
                url: `${import.meta.env.REACT_APP_IZIPLAY_URL_SUBTITLE}/sub/${sub.id}/vtt`,
                id: sub.id,
                lang: `${sub.lang} ${i < 10 ? `0${i.toString()}` : i} ${sub.provider}`,
                langId: sub.langId
              }))
            )
            .catch(() => [])
        )))
        .then(results => this.setState({ subs: results.flat() }))
    }
  }

  async ffprobe() {
    const res = await apiHls().get(`/${this.props.params.file}/ffprobe`)
    this.setState({ ffprobe: res.data })
    const probeDuration = parseFloat(res.data.format.duration)
    this.probeDuration = probeDuration
  }

  onPlayPause(event: 'start' | 'stop') {
    rtSend(event, {
      progress: ((this.video.current?.currentTime ?? 0) / this.probeDuration) * 100,
      ...this.media
    })
  }

  addUploadedSub(sub: any) {
    this.setState({
      subs: [
        ...this.state.subs,
        {
          url: `${import.meta.env.REACT_APP_IZIPLAY_URL_SUBTITLE}/sub/${sub.id}/vtt`,
          id: sub.id,
          lang: `${sub.lang} internal uploaded`,
          langId: sub.lang
        }
      ]
    })
  }

  thumbnailsUrl() {
    return `${this.state.server}/${this.props.params.file}/thumbs/master.vtt?withPosition=true`
  }

  render() {
    const { step, downloading, transcoding, launched, castStatus, subs } = this.state
    const done = step === this.stages.length - 1
    const stopped = this.state.stopped !== undefined && !done
    const percent = downloading ? Number(downloading.percent) : NaN
    const canCast = Boolean((window as any).cast)

    return (
      <div className="hls scrollable">
        <div className="hls-frame">
          <VideoPlayer>
            <VideoSkin
              className="hls-player"
              // Storyboard cues are relative to the VTT; Video.js would resolve them against the page.
              renderThumbnail={props => {
                const { src } = props as ImgHTMLAttributes<HTMLImageElement>
                return <img {...props} alt="" src={src && new URL(src, this.thumbnailsUrl()).href} />
              }}
            >
              {/*
                * The playlist is an EVENT one that grows while the transcoder
                * runs, so hls.js reads it as live. It is a file: start at zero,
                * and put the live sync point out of reach so neither hls.js nor
                * Video.js's first-play seek jumps to the live edge.
                */}
              <HlsJsVideo
                crossOrigin="anonymous"
                playsInline
                streamType="on-demand"
                source={{
                  src: `${this.state.server}/${this.props.params.file}/master.m3u8`,
                  engine: {
                    hlsJs: {
                      startPosition: 0,
                      liveSyncDuration: Number.MAX_SAFE_INTEGER,
                      liveMaxLatencyDuration: Infinity
                    }
                  }
                }}
                mediaRef={this.video}
                onPlay={() => this.onPlayPause('start')}
                onPause={() => this.onPlayPause('stop')}
              >
                <track
                  src={`${this.state.server}/${this.props.params.file}/chapters/master.vtt`}
                  kind="chapters"
                  default
                />
                <track
                  src={this.thumbnailsUrl()}
                  kind="metadata"
                  label="thumbnails"
                  default
                />
                {/** TODO: add file subtitles */}
                {subs.map((subtitle: any) =>
                  <track
                    key={subtitle.id}
                    src={subtitle.url}
                    kind="subtitles"
                    label={subtitle.lang}
                    srcLang={subtitle.langId}
                  />
                )}
              </HlsJsVideo>
            </VideoSkin>
          </VideoPlayer>
        </div>

        <Card className="hls-status">
          <CardContent>
            <div className="hls-status__header">
              <div className="hls-status__title">
                <Typography variant="h6">Stream</Typography>
                {stopped ? <Chip size="small" color="error" label="Stopped" /> : null}
              </div>
              <div className="hls-actions">
                {canCast ? (
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={Boolean(launched)}
                    startIcon={launched ? <CastConnected size={18} /> : <Cast size={18} />}
                    onClick={this.startCast.bind(this)}
                  >
                    {launched ? 'Casting' : 'Cast'}
                  </Button>
                ) : null}
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<UploadFile size={18} />}
                  onClick={this.openSubtitleModal.bind(this)}
                >
                  Import subtitle
                </Button>
              </div>
            </div>

            <Stepper activeStep={step}>
              {this.stages.map((stage, i) => (
                <Step key={stage.label} aria-current={i === step && !done && !stopped ? 'step' : undefined}>
                  <StepLabel>{stage.label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {downloading ? (
              <div className="hls-download hls-reveal">
                <div className="hls-download__row">
                  <span>Download</span>
                  <span>{Number.isFinite(percent) ? `${percent.toFixed(2)}%` : '…'}</span>
                </div>
                <LinearProgress value={Number.isFinite(percent) ? percent : undefined} />
              </div>
            ) : null}

            {transcoding ? (
              <div className="hls-metrics hls-reveal">
                <div className="hls-metric">
                  <span className="hls-metric__label">Transcoding speed</span>
                  <span className="hls-metric__value">{transcoding.speed}</span>
                </div>
                <div className="hls-metric">
                  <span className="hls-metric__label">Transcoded</span>
                  <span className="hls-metric__value">{transcoding.time}</span>
                </div>
              </div>
            ) : null}

            {launched ? (
              <div className="hls-cast hls-reveal">
                <IconButton aria-label="play/pause cast" onClick={this.castPlayPause.bind(this)}>
                  {castStatus.playerState === 'PAUSED' ? <PlayArrow size={24} /> : <Pause size={24} />}
                </IconButton>
                <Select<number>
                  label="Cast subtitle"
                  onChange={id => this.changeCastSub(id)}
                  options={subs.map((sub: any, i: number) => ({ value: i + 1, label: sub.lang }))}
                />
                <Chip size="small" label={castStatus.playerState ?? 'Connected'} />
              </div>
            ) : null}
          </CardContent>
        </Card>

        <SubtitleModal
          fileId={this.props.params.file}
          open={this.state.isSubtitleModalOpen}
          onClose={this.closeSubtitleModal.bind(this)}
          onUpload={this.addUploadedSub.bind(this)}
        />
      </div>
    )
  }
}

/*
 * The player is still a class, so route params are injected rather than read
 * from a hook.
 */
export default function HlsPlayerRoute() {
  const params = useParams()
  // The /hls/:file route cannot match without a file segment.
  return <HlsPlayer params={{ ...params, file: params.file! }} />
}
